import { MicVAD } from '@ricky0123/vad-web';
import { message } from 'antd';
import { useCallback, useRef, useState } from 'react';

import type { CommonResponse } from '@/types/responseModel';
import { GET } from '@/utils/request';

/**
 * Aliyun ASR Hook - Streaming Speech Recognition
 * Based on Aliyun NLS (Natural Language Service)
 *
 * Reference: Frontend-MindApp/src/features/speech/aliyun/SpeechRecognition.ts
 */

interface StreamingPlanner {
  streamPlatform: string;
  platformParam: {
    appKey: string;
  };
  wsUrl: string;
  token: string;
  expireTime: number;
  customParam?: Record<string, unknown>;
}

interface TranscriptionResult {
  index: number;
  time: number;
  result: string;
  status: number;
}

interface AliyunMessage {
  header: {
    message_id: string;
    task_id: string;
    namespace: string;
    name: string;
    status: number;
    status_message: string;
  };
  payload?: TranscriptionResult;
}

export interface UseAliyunASROptions {
  onTranscript?: (text: string, isFinal: boolean) => void;
  onError?: (error: string) => void;
  onConnectionStateChange?: (state: 'connecting' | 'connected' | 'disconnected' | 'error') => void;
  onVADSend?: (text: string) => void; // Called when VAD detects silence and auto-sends
  vadSilenceMs?: number; // Silence duration to trigger send (default 500ms)
}

export interface UseAliyunASRReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string>;
  cancelRecording: () => void;
  isRecording: boolean;
  isConnected: boolean;
  transcript: string;
  error: string | null;
}

function generateRandomHex(length: number): string {
  const chars = '0123456789abcdef';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }

  return result;
}

// Audio worklet processor code for capturing audio
const AUDIO_WORKLET_CODE = `
class AudioProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.buffer = [];
    this.bufferSize = 3200; // 100ms @ 16kHz 16bit
  }

  process(inputs) {
    const input = inputs[0];
    if (input && input[0]) {
      const samples = input[0];
      // Convert float32 to int16
      for (let i = 0; i < samples.length; i++) {
        const s = Math.max(-1, Math.min(1, samples[i]));
        this.buffer.push(s < 0 ? s * 0x8000 : s * 0x7FFF);
      }

      // Send when buffer is full
      while (this.buffer.length >= this.bufferSize / 2) {
        const chunk = this.buffer.splice(0, this.bufferSize / 2);
        const int16Array = new Int16Array(chunk);
        this.port.postMessage(int16Array.buffer);
      }
    }
    return true;
  }
}

registerProcessor('aliyun-asr-processor', AudioProcessor);
`;

export function useAliyunASR({
  onTranscript,
  onError,
  onConnectionStateChange,
  onVADSend,
  vadSilenceMs = 500
}: UseAliyunASROptions = {}): UseAliyunASRReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const resultsRef = useRef<TranscriptionResult[]>([]);
  const taskIdRef = useRef<string>('');
  const appKeyRef = useRef<string>('');
  const customParamRef = useRef<Record<string, unknown> | undefined>(undefined);
  const processTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioBufferRef = useRef<Int16Array[]>([]);
  const stopProcessFlagRef = useRef(false);
  const completeResolverRef = useRef<((text: string) => void) | null>(null);

  // Timing: track when user stops speaking (last audio sent) and first result received
  const lastAudioSentTimeRef = useRef<number>(0);
  const firstResultReceivedRef = useRef<boolean>(false);

  // VAD: Silero VAD instance and state
  const vadRef = useRef<MicVAD | null>(null);
  const hasSpeechRef = useRef<boolean>(false); // Whether we've detected any speech in current session
  const vadSentRef = useRef<boolean>(false); // Whether we've already sent for current speech segment
  const speechEndTimeRef = useRef<number>(0); // When VAD detected speech end
  const vadSendTimerRef = useRef<NodeJS.Timeout | null>(null); // Timer for delayed send after speech end

  /**
   * Get current combined result
   */
  const getCurrentResult = useCallback(() => {
    return resultsRef.current.map((item) => item.result).join('');
  }, []);

  /**
   * Merge Int16Arrays
   */
  const mergeInt16Arrays = useCallback((arrays: Int16Array[]): Int16Array => {
    const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
    const result = new Int16Array(totalLength);
    let offset = 0;

    arrays.forEach((arr) => {
      result.set(arr, offset);
      offset += arr.length;
    });

    return result;
  }, []);

  /**
   * Send command to WebSocket
   */
  const sendCommand = useCallback((command: string, data?: unknown) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const messageId = generateRandomHex(32).toLowerCase();

    wsRef.current.send(
      JSON.stringify({
        header: {
          message_id: messageId,
          task_id: taskIdRef.current,
          namespace: 'SpeechTranscriber',
          name: command,
          appkey: appKeyRef.current
        },
        payload: data
      })
    );
  }, []);

  /**
   * Start transcription command
   */
  const startTranscription = useCallback(() => {
    taskIdRef.current = generateRandomHex(32).toLowerCase();

    if (customParamRef.current) {
      sendCommand('StartTranscription', customParamRef.current);
    } else {
      sendCommand('StartTranscription', {
        format: 'pcm',
        sample_rate: 16000,
        enable_intermediate_result: true,
        enable_punctuation_prediction: true,
        enable_inverse_text_normalization: true
      });
    }
  }, [sendCommand]);

  /**
   * Stop transcription command
   */
  const stopTranscription = useCallback(() => {
    sendCommand('StopTranscription');
  }, [sendCommand]);

  /**
   * Process audio buffer - send at most every 20ms
   */
  /**
   * Clear current transcript and results for next round
   */
  const clearCurrentTranscript = useCallback(() => {
    resultsRef.current = [];
    setTranscript('');
    hasSpeechRef.current = false;
    vadSentRef.current = false;
    firstResultReceivedRef.current = false;
    lastAudioSentTimeRef.current = 0;
  }, []);

  /**
   * Handle VAD speech end - schedule send after delay
   */
  const handleVADSpeechEnd = useCallback(() => {
    console.log('🔇 VAD: Speech ended');
    speechEndTimeRef.current = Date.now();

    // Clear any existing timer
    if (vadSendTimerRef.current) {
      clearTimeout(vadSendTimerRef.current);
    }

    // Schedule send after vadSilenceMs delay
    vadSendTimerRef.current = setTimeout(() => {
      if (hasSpeechRef.current && !vadSentRef.current) {
        const currentText = getCurrentResult();

        if (currentText.trim()) {
          const latency = Date.now() - speechEndTimeRef.current;

          console.log(`🔇 VAD: Sending after ${latency}ms silence: "${currentText}"`);
          vadSentRef.current = true;
          onVADSend?.(currentText);

          // Clear for next round
          clearCurrentTranscript();
        }
      }
    }, vadSilenceMs);
  }, [vadSilenceMs, getCurrentResult, onVADSend, clearCurrentTranscript]);

  /**
   * Handle VAD speech start - cancel pending send
   */
  const handleVADSpeechStart = useCallback(() => {
    console.log('🎤 VAD: Speech started');
    hasSpeechRef.current = true;
    vadSentRef.current = false;

    // Cancel pending send if user starts speaking again
    if (vadSendTimerRef.current) {
      clearTimeout(vadSendTimerRef.current);
      vadSendTimerRef.current = null;
    }
  }, []);

  /**
   * Initialize and start Silero VAD
   */
  const startVAD = useCallback(async () => {
    if (vadRef.current) {
      vadRef.current.start();

      return;
    }

    try {
      const vad = await MicVAD.new({
        onSpeechStart: handleVADSpeechStart,
        onSpeechEnd: handleVADSpeechEnd
      });

      vadRef.current = vad;
      vad.start();
      console.log('✅ Silero VAD initialized and started');
    } catch (err) {
      console.error('Failed to initialize VAD:', err);
    }
  }, [handleVADSpeechStart, handleVADSpeechEnd]);

  /**
   * Stop and destroy VAD
   */
  const stopVAD = useCallback(() => {
    if (vadSendTimerRef.current) {
      clearTimeout(vadSendTimerRef.current);
      vadSendTimerRef.current = null;
    }

    if (vadRef.current) {
      vadRef.current.destroy();
      vadRef.current = null;
      console.log('🛑 VAD stopped');
    }
  }, []);

  const startProcessing = useCallback(() => {
    if (processTimerRef.current) {
      clearInterval(processTimerRef.current);
    }

    // Start Silero VAD when processing starts
    startVAD();

    processTimerRef.current = setInterval(() => {
      if (audioBufferRef.current.length > 0) {
        const content = mergeInt16Arrays(audioBufferRef.current);

        audioBufferRef.current = [];

        if (content.length > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(content);
          // Record time when audio is sent
          lastAudioSentTimeRef.current = Date.now();
        }
      } else if (stopProcessFlagRef.current) {
        if (processTimerRef.current) {
          clearInterval(processTimerRef.current);
          processTimerRef.current = null;
        }

        stopVAD();
        stopTranscription();
      }
    }, 20);
  }, [mergeInt16Arrays, stopTranscription, startVAD, stopVAD]);

  /**
   * Handle WebSocket message
   */
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const response = JSON.parse(event.data) as AliyunMessage;

        console.log('Aliyun ASR message:', response.header.name);

        switch (response.header.name) {
          case 'TranscriptionStarted':
            console.log('Transcription started');
            startProcessing();

            break;

          case 'TranscriptionResultChanged':
          case 'SentenceEnd': {
            if (response.payload) {
              // Calculate latency for first result
              if (!firstResultReceivedRef.current && lastAudioSentTimeRef.current > 0) {
                const latency = Date.now() - lastAudioSentTimeRef.current;

                console.log(
                  `🎤 ASR First Result Latency: ${latency}ms (from last audio sent to first result)`
                );
                firstResultReceivedRef.current = true;
              }

              // VAD: Mark that we have speech (Silero VAD handles timing)
              hasSpeechRef.current = true;

              const targetIndex = resultsRef.current.findIndex(
                (item) => item.index === response.payload!.index
              );

              if (targetIndex > -1) {
                if (response.payload.result) {
                  resultsRef.current[targetIndex] = response.payload;
                }
              } else {
                resultsRef.current.push(response.payload);
              }

              const currentText = getCurrentResult();

              setTranscript(currentText);
              onTranscript?.(currentText, response.header.name === 'SentenceEnd');
            }

            break;
          }

          case 'TranscriptionCompleted':
          case 'TaskFailed': {
            const finalText = getCurrentResult();

            console.log('Transcription completed:', finalText);
            completeResolverRef.current?.(finalText);
            completeResolverRef.current = null;
            wsRef.current?.close();

            break;
          }
        }
      } catch (err) {
        console.error('Failed to parse ASR message:', err);
      }
    },
    [getCurrentResult, onTranscript, startProcessing]
  );

  /**
   * Fetch streaming planner from backend
   */
  const fetchStreamingPlanner = useCallback(async (): Promise<StreamingPlanner> => {
    const response = await GET<CommonResponse<StreamingPlanner>>({
      url: '/rest/os/voice/streaming/planner',
      params: { language: 'zh' }
    });

    if (!response.data.data) {
      throw new Error('Failed to get streaming planner');
    }

    return response.data.data;
  }, []);

  /**
   * Start recording
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setTranscript('');
      resultsRef.current = [];
      audioBufferRef.current = [];
      stopProcessFlagRef.current = false;
      lastAudioSentTimeRef.current = 0;
      firstResultReceivedRef.current = false;
      // Reset VAD state
      hasSpeechRef.current = false;
      vadSentRef.current = false;
      speechEndTimeRef.current = 0;

      onConnectionStateChange?.('connecting');

      // Get streaming planner from backend
      const planner = await fetchStreamingPlanner();

      console.log('Streaming planner response:', planner);

      if (!planner.platformParam.appKey) {
        throw new Error('Invalid streaming planner response: missing appKey');
      }

      appKeyRef.current = planner.platformParam.appKey;
      customParamRef.current = planner.customParam;

      const wsUrl = decodeURIComponent(planner.wsUrl);

      console.log('Connecting to Aliyun ASR:', wsUrl);

      // Create WebSocket connection
      const ws = new WebSocket(wsUrl);

      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Aliyun ASR WebSocket connected');
        setIsConnected(true);
        onConnectionStateChange?.('connected');
        startTranscription();
      };

      ws.onmessage = handleMessage;

      ws.onerror = () => {
        const errMsg = 'ASR WebSocket error';

        setError(errMsg);
        onError?.(errMsg);
        onConnectionStateChange?.('error');
      };

      ws.onclose = () => {
        console.log('Aliyun ASR WebSocket closed');
        setIsConnected(false);
        setIsRecording(false);
        onConnectionStateChange?.('disconnected');
      };

      // Wait for connection
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Connection timeout')), 10000);

        ws.onopen = () => {
          clearTimeout(timeout);
          setIsConnected(true);
          onConnectionStateChange?.('connected');
          startTranscription();
          resolve();
        };

        ws.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Connection failed'));
        };
      });

      // Start audio capture
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      mediaStreamRef.current = stream;

      const audioContext = new AudioContext({ sampleRate: 16000 });

      audioContextRef.current = audioContext;

      // Create audio worklet
      const blob = new Blob([AUDIO_WORKLET_CODE], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);

      await audioContext.audioWorklet.addModule(workletUrl);
      URL.revokeObjectURL(workletUrl);

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, 'aliyun-asr-processor');

      workletNode.port.onmessage = (event) => {
        const audioData = new Int16Array(event.data);

        audioBufferRef.current.push(audioData);
      };

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);
      workletNodeRef.current = workletNode;

      setIsRecording(true);
      message.success('开始录音');
    } catch (err) {
      console.error('Failed to start recording:', err);
      const errMsg = err instanceof Error ? err.message : 'Failed to start recording';

      setError(errMsg);
      onError?.(errMsg);
      onConnectionStateChange?.('error');
    }
  }, [fetchStreamingPlanner, handleMessage, onConnectionStateChange, onError, startTranscription]);

  /**
   * Stop recording and get final result
   */
  const stopRecording = useCallback(async (): Promise<string> => {
    // Stop audio capture
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    setIsRecording(false);

    // Signal to stop processing and send stop command
    stopProcessFlagRef.current = true;

    // Wait for transcription to complete
    return new Promise<string>((resolve) => {
      completeResolverRef.current = resolve;

      // Timeout fallback
      setTimeout(() => {
        if (completeResolverRef.current) {
          completeResolverRef.current(getCurrentResult());
          completeResolverRef.current = null;
        }
      }, 3000);
    });
  }, [getCurrentResult]);

  /**
   * Cancel recording without getting result
   */
  const cancelRecording = useCallback(() => {
    // Stop audio capture
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (processTimerRef.current) {
      clearInterval(processTimerRef.current);
      processTimerRef.current = null;
    }

    // Stop VAD
    stopVAD();

    // Close WebSocket
    wsRef.current?.close();
    wsRef.current = null;

    resultsRef.current = [];
    setTranscript('');
    setIsRecording(false);
    setIsConnected(false);
  }, [stopVAD]);

  return {
    startRecording,
    stopRecording,
    cancelRecording,
    isRecording,
    isConnected,
    transcript,
    error
  };
}
